import React, { useState, useEffect } from "react";
import { Segment, Header, Button, Icon, Message, Grid, Loader } from "semantic-ui-react";
import { useOrgId } from "../auth";
import { useFluidFormConfigClient } from "./fluidFormConfig.client";
import { PublishedVersionsSection } from "./PublishedVersionsSection";
import { InComponentErrorRenderer } from "../components/InComponentErrorRenderer";
import { trackEvent } from '../analytics';
export function PublishTabContent({ formKey, onConfigRecordChange }) {
    const [copiedEmbed, setCopiedEmbed] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);
    const [signature, setSignature] = useState(null);
    const [signatureLoading, setSignatureLoading] = useState(false);
    const [signatureError, setSignatureError] = useState(null);
    const [publishLoading, setPublishLoading] = useState(false);
    const [publishError, setPublishError] = useState(null);
    const [publishSuccess, setPublishSuccess] = useState(null);
    const [latestPublished, setLatestPublished] = useState(null);
    const [latestPublishedError, setLatestPublishedError] = useState(null);
    const [versionsRefreshKey, setVersionsRefreshKey] = useState(0);
    const orgId = useOrgId();
    const configClient = useFluidFormConfigClient();
    const origin = window.location.origin;
    const baseUrl = `${origin}/ff/${orgId}/${formKey}`;
    const iframeUrl = signature ? `${baseUrl}?sig=${signature}&mode=headless&chatPath=chat-router` : baseUrl;
    const standaloneUrl = signature ? `${baseUrl}?sig=${signature}` : baseUrl;
    // Generate signature on component mount
    useEffect(() => {
        generateSignature();
        loadLatestPublished();
    }, [formKey]);
    const loadLatestPublished = async () => {
        try {
            setLatestPublishedError(null);
            const cfg = await configClient.getFormConfig(formKey);
            const versions = (cfg.versions || []);
            const activeId = cfg.currentVersionId;
            setLatestPublished(activeId ? (versions.find(v => v.versionId === activeId) || versions[versions.length - 1]) : (versions.length > 0 ? versions[versions.length - 1] : null));
            return { activeId, versions };
        }
        catch (e) {
            setLatestPublished(null);
            setLatestPublishedError('Failed to load latest published version. Please try again.');
            return null;
        }
    };
    const generateSignature = async () => {
        setSignatureLoading(true);
        setSignatureError(null);
        try {
            const signatureData = await configClient.generateFormSignature(formKey);
            setSignature(signatureData.signature);
        }
        catch (error) {
            setSignatureError('Failed to generate signature for public access');
        }
        finally {
            setSignatureLoading(false);
        }
    };
    const publish = async () => {
        setPublishLoading(true);
        setPublishError(null);
        setPublishSuccess(null);
        try {
            const result = await configClient.publishForm(formKey);
            trackEvent('publish_form', { form_key: formKey, version_id: result.versionId });
            setPublishSuccess(`Published version ${result.versionId}`);
            await generateSignature();
            const versionInfo = await loadLatestPublished();
            setVersionsRefreshKey((x) => x + 1);
            if (versionInfo) {
                onConfigRecordChange?.({ currentVersionId: versionInfo.activeId, versions: versionInfo.versions });
            }
        }
        catch (error) {
            console.error('Error publishing form:', error);
            setPublishError('Failed to publish form');
        }
        finally {
            setPublishLoading(false);
        }
    };
    // Generate embed code with the current form ID
    const embedCode = `<div id="fluidform-embed-${formKey}" style="position:relative;">
    <div id="fluidform-embed-basic" style="position:relative;">
    <script src="${origin}/fluid-navigate.js"></script>
    <iframe id="fluidform-iframe-${formKey}"
        onload="loadIframe(this)"
        src="${iframeUrl}"
        width="100%"
        title="Form basic"
        frameborder="0"
        scrolling="no"
        allowtransparency="true"
        style="border:0;background:transparent;width:100%;min-height:500px;overflow:hidden;display:block;">
        </iframe>
        </div>
</div>`;
    // Function to copy embed code to clipboard
    const copyEmbedCode = () => {
        navigator.clipboard.writeText(embedCode)
            .then(() => {
            setCopiedEmbed(true);
            setTimeout(() => setCopiedEmbed(false), 2000);
        })
            .catch(err => {
            console.error('Failed to copy: ', err);
        });
    };
    // Function to copy direct link to clipboard
    const copyDirectLink = () => {
        const directLink = `${standaloneUrl}`;
        navigator.clipboard.writeText(directLink)
            .then(() => {
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        })
            .catch(err => {
            console.error('Failed to copy: ', err);
        });
    };
    const openInNewTab = () => {
        window.open(`${standaloneUrl}`, '_blank');
    };
    return (React.createElement("div", { style: { padding: '1rem' } },
        React.createElement(Grid, { stackable: true },
            (publishError || publishSuccess) && (React.createElement(Grid.Column, { width: 16 },
                publishError && React.createElement(InComponentErrorRenderer, { error: publishError, headerText: "Publish Error" }),
                publishSuccess && (React.createElement(Message, { positive: true },
                    React.createElement(Message.Header, null, "Published"),
                    React.createElement("p", null, publishSuccess))))),
            React.createElement(Grid.Column, { width: 16 },
                React.createElement(Segment, { style: { boxShadow: 'none', border: '1px solid #f0f0f0' }, "data-tour-id": "builder-publish-button" },
                    React.createElement(Header, { as: "h4", style: { fontWeight: 500, fontSize: '0.95em', marginBottom: '1em' } }, "Publish"),
                    React.createElement("p", { style: { marginTop: 0, color: '#666', fontSize: '0.9em' } }, "Publish the current form configuration so your users can see it."),
                    (latestPublished || latestPublishedError) && (React.createElement(Message, { negative: !!latestPublishedError, style: {
                            boxShadow: 'none',
                            background: '#f9f9f9',
                            fontSize: '0.9em',
                            padding: '1em'
                        } },
                        React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: '6px' } }, latestPublished ? React.createElement(React.Fragment, null,
                            React.createElement("div", null,
                                React.createElement("strong", null, "Existing version:"),
                                " ",
                                React.createElement("code", null, latestPublished.versionId)),
                            React.createElement("div", null,
                                React.createElement("strong", null, "Published at:"),
                                " ",
                                new Date(latestPublished.createdAt).toLocaleString()),
                            React.createElement("div", null,
                                React.createElement("strong", null, "By:"),
                                " ",
                                latestPublished.publishedBy || '-')) : React.createElement("div", null, latestPublishedError)))),
                    React.createElement(Button, { primary: true, onClick: publish, loading: publishLoading },
                        React.createElement(Icon, { name: "upload" }),
                        "Publish for users"),
                    React.createElement(PublishedVersionsSection, { formKey: formKey, configClient: configClient, refreshKey: versionsRefreshKey, onConfigRecordChange: onConfigRecordChange }))),
            (latestPublished && !latestPublishedError) && React.createElement(React.Fragment, null,
                signatureError && (React.createElement(Grid.Column, { width: 16 },
                    React.createElement(Message, { negative: true },
                        React.createElement(Message.Header, null, "Signature Generation Error"),
                        React.createElement("p", null, signatureError),
                        React.createElement(Button, { size: "small", onClick: generateSignature, loading: signatureLoading }, "Retry Signature Generation")))),
                React.createElement(Grid.Column, { width: 16, style: { marginTop: '1em' } },
                    React.createElement(Segment, { style: { boxShadow: 'none', border: '1px solid #f0f0f0' } },
                        React.createElement(Header, { as: "h4", style: { fontWeight: 500, fontSize: '0.95em', marginBottom: '1em' } },
                            "Direct link",
                            signatureLoading && React.createElement(Loader, { active: true, inline: true, size: "mini", style: { marginLeft: '10px' } })),
                        React.createElement(Message, { style: {
                                boxShadow: 'none',
                                background: '#f9f9f9',
                                fontSize: '0.9em',
                                padding: '1em'
                            } },
                            React.createElement("code", { style: { wordBreak: 'break-all', fontFamily: 'monospace' } }, standaloneUrl),
                            React.createElement(Button, { size: "tiny", icon: true, floated: "right", onClick: copyDirectLink, color: copiedLink ? "green" : undefined, style: {
                                    boxShadow: 'none',
                                    background: copiedLink ? undefined : 'white',
                                    border: copiedLink ? undefined : '1px solid #f0f0f0',
                                    marginTop: '-7px'
                                } },
                                React.createElement(Icon, { name: copiedLink ? "check" : "copy" }),
                                copiedLink ? '' : ''),
                            React.createElement(Button, { size: "tiny", icon: true, floated: "right", onClick: openInNewTab, color: "blue", style: {
                                    boxShadow: 'none',
                                    background: 'white',
                                    border: '1px solid #f0f0f0',
                                    marginTop: '-7px'
                                } },
                                React.createElement(Icon, { name: "external", color: "grey" }))))),
                React.createElement(Grid.Column, { width: 16 },
                    React.createElement(Segment, { style: { boxShadow: 'none', border: '1px solid #f0f0f0' } },
                        React.createElement(Header, { as: "h4", style: { fontWeight: 500, fontSize: '0.95em', marginBottom: '1em' } }, "Embed on your site"),
                        React.createElement("div", { style: { position: 'relative' } },
                            React.createElement("div", { style: { position: 'absolute', top: '8px', right: '8px', zIndex: 1 } },
                                React.createElement(Button, { size: "tiny", icon: true, onClick: copyEmbedCode, color: copiedEmbed ? "green" : undefined, style: {
                                        boxShadow: 'none',
                                        background: copiedEmbed ? undefined : 'white',
                                        border: copiedEmbed ? undefined : '1px solid #f0f0f0'
                                    } },
                                    React.createElement(Icon, { name: copiedEmbed ? "check" : "copy" }),
                                    copiedEmbed ? 'Copied!' : 'Copy')),
                            React.createElement("pre", { style: {
                                    backgroundColor: '#2d2d2d',
                                    color: '#cccccc',
                                    padding: '16px',
                                    borderRadius: '3px',
                                    overflow: 'auto',
                                    fontSize: '13px',
                                    lineHeight: '1.5',
                                    margin: 0,
                                    fontFamily: 'SFMono-Regular, Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace'
                                } },
                                React.createElement("code", null, embedCode)))))))));
}
